/** The selected member conversation, backed by paged history and short polling. */

import {
  ChatComposer,
  ChatMessageList,
  type ChatMessageView,
  ErrorState,
  LoadingState,
} from "@elyxeer/ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocale } from "~/i18n";
import {
  ApiError,
  fetchAdminChatThread,
  markAdminChatThreadRead,
  sendAdminChatMessage,
} from "~/lib/api";
import { chatMemberName, uniqueChatMessages } from "~/lib/chat";
import { queryKeys } from "~/lib/query-client";

const MESSAGE_PAGE_SIZE = 50;
const CONVERSATION_POLL_MS = 5_000;

interface AdminChatConversationProps {
  threadId: string;
  onBack: () => void;
}

/** Messages are owned by the administrator when they were sent by any team member. */
function asMessageView(
  message: {
    id: string;
    body: string;
    created_at: string;
    sender_role: "member" | "admin";
    sender_display_name: string | null;
  },
  memberName: string,
  teamName: string,
): ChatMessageView {
  const isOwn = message.sender_role === "admin";
  return {
    id: message.id,
    body: message.body,
    createdAt: message.created_at,
    isOwn,
    senderLabel: isOwn
      ? message.sender_display_name || teamName
      : message.sender_display_name || memberName,
  };
}

export function AdminChatConversation({
  threadId,
  onBack,
}: AdminChatConversationProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const attemptedReadMarker = useRef<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: queryKeys.chatThread(threadId),
    queryFn: ({ pageParam, signal }) =>
      fetchAdminChatThread(threadId, {
        limit: MESSAGE_PAGE_SIZE,
        offset: pageParam,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.messages.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    refetchInterval: CONVERSATION_POLL_MS,
  });

  const { mutate: markRead } = useMutation({
    mutationFn: () => markAdminChatThreadRead(threadId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatThreadsRoot,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatThread(threadId),
      });
    },
  });

  const sendMutation = useMutation({
    mutationFn: (body: string) => sendAdminChatMessage(threadId, body),
    onSuccess: () => {
      setDraft("");
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatThreadsRoot,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatThread(threadId),
      });
    },
  });

  const detail = query.data?.pages[0];
  const readMarker =
    detail && detail.unread_count > 0
      ? `${threadId}:${detail.last_message?.id ?? detail.updated_at}`
      : null;

  useEffect(() => {
    if (!readMarker || attemptedReadMarker.current === readMarker) return;
    attemptedReadMarker.current = readMarker;
    markRead();
  }, [markRead, readMarker]);

  const messages = useMemo(() => {
    if (!query.data || !detail) return [];
    const memberName = chatMemberName(detail);
    return uniqueChatMessages(query.data.pages).map((message) =>
      asMessageView(message, memberName, t("chat.team")),
    );
  }, [detail, query.data, t]);

  const send = () => {
    const body = draft.trim();
    if (!body || sendMutation.isPending) return;
    sendMutation.mutate(body);
  };

  const changeDraft = (value: string) => {
    if (sendMutation.isError) sendMutation.reset();
    setDraft(value);
  };

  if (query.isPending) {
    return (
      <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
        <LoadingState />
      </Box>
    );
  }

  if (query.isError || !detail) {
    return (
      <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
        <ErrorState
          message={t("chat.errors.load")}
          onRetry={() => void query.refetch()}
        />
      </Box>
    );
  }

  const memberName = chatMemberName(detail);
  const inactive = !detail.user_is_active;
  const sendError = sendMutation.isError
    ? t(
        sendMutation.error instanceof ApiError &&
          sendMutation.error.status === 409
          ? "chat.errors.inactive"
          : "chat.errors.send",
      )
    : null;
  const dateOptions = {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  } as const;

  return (
    <Box
      data-testid="chat.conversation"
      sx={{
        display: "flex",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          minHeight: 72,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          paddingInline: { xs: 1.5, sm: 2.5 },
          paddingBlock: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Tooltip title={t("common.back")}>
          <IconButton
            onClick={onBack}
            aria-label={t("common.back")}
            data-testid="chat.back"
            sx={{ display: { xs: "inline-flex", md: "none" } }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="L1"
            sx={{
              color: "text.primary",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {memberName}
          </Typography>
          {detail.user_display_name ? (
            <Typography
              variant="T3"
              sx={{
                color: "text.muted",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {detail.user_email}
            </Typography>
          ) : null}
        </Box>
        {inactive ? (
          <Chip size="small" variant="outlined" label={t("chat.inactive")} />
        ) : null}
      </Box>

      {query.hasNextPage ? (
        <Box sx={{ display: "flex", justifyContent: "center", paddingTop: 1 }}>
          <Button
            size="small"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            data-testid="chat.load-earlier"
          >
            {t("chat.loadEarlier")}
          </Button>
        </Box>
      ) : null}

      <ChatMessageList
        messages={messages}
        emptyMessage={t("chat.emptyMessages")}
        formatTime={(value) => formatDate(value, dateOptions)}
        ariaLabel={t("chat.conversationLabel", { name: memberName })}
      />

      {inactive ? (
        <Alert severity="warning" sx={{ marginInline: 2, marginBottom: 1 }}>
          {t("chat.inactiveHint")}
        </Alert>
      ) : null}

      <ChatComposer
        value={draft}
        onChange={changeDraft}
        onSend={send}
        placeholder={t("chat.messagePlaceholder")}
        sendLabel={t("chat.send")}
        disabled={inactive}
        isSending={sendMutation.isPending}
        error={sendError}
        maxLength={4000}
        testId="chat.composer"
      />
    </Box>
  );
}
